import type { AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";

export interface RecommendationProvenance {
  version: 1;
  priority: string;
  traceId: string;
  appliedAt: string;
  verification: NonNullable<AIEquipmentSuggestionOption["verification"]>;
  metrics: NonNullable<AIEquipmentSuggestionOption["verifiedMetrics"]>;
  warnings: string[];
  pros: string[];
  cons: string[];
  completionRequirements?: AIEquipmentSuggestionOption["completionRequirements"];
  designPlan?: AIEquipmentSuggestionOption["designPlan"];
  technicalEvidence?: AIEquipmentSuggestionOption["technicalEvidence"];
  operatingParameters?: Record<string, number>;
  appliedEquipmentSignature?: string;
}

// Ignores revision/journal identities but records persisted equipment choices,
// ordering, stages and lengths. The report can identify later engineering edits.
export function equipmentSelectionSignature(equipment: Record<string, any> | null | undefined): string {
  if (!equipment) return "";
  const single = ["motor_id", "mle_id", "sensor_id", "vsd_id", "switchboard_id", "up_trans_id", "down_trans_id", "use_vsd"];
  const selections: Record<string, unknown> = Object.fromEntries(single.map(key => [key, equipment[key] ?? null]));
  for (const name of ["pumps", "intakes", "seals", "cables"]) {
    const rows = equipment[`r_xref_${name}`] || [];
    selections[name] = (equipment[`${name}_order`] || []).map((id: string) => {
      const row = rows.find((item: Record<string, unknown>) => item.id === id) || {};
      return [row[`${name.slice(0, -1)}_id`] ?? null, row.total_stages ?? null, row.length_feet ?? null, row.use_high_strength_shaft ?? false,
        name === "pumps" ? (row.housings_order || []).map((housingId: string) => {
          const housing = (row.r_xref_housings || []).find((item: Record<string, unknown>) => item.id === housingId) || {};
          return [housing.housing_id, housing.stage_count, housing.length_inches];
        }) : null];
    });
  }
  return JSON.stringify(selections);
}

export function recommendationProvenance(option: AIEquipmentSuggestionOption): RecommendationProvenance {
  if (!option.traceId) throw new Error("Recommendation trace is required.");
  return {
    version: 1,
    priority: option.priority,
    traceId: option.traceId,
    appliedAt: new Date().toISOString(),
    verification: option.verification || {},
    metrics: option.verifiedMetrics || {},
    warnings: option.warnings || [],
    pros: option.pros || [],
    cons: option.cons || [],
    completionRequirements: option.completionRequirements || [],
    designPlan: option.designPlan,
    technicalEvidence: option.technicalEvidence,
    operatingParameters: option.sizingDraft?.parameters
  };
}

export function readRecommendationProvenance(value: unknown): RecommendationProvenance | null {
  if (!value || typeof value !== "object") return null;
  const data = value as RecommendationProvenance;
  return data.version === 1 && typeof data.traceId === "string" &&
    typeof data.priority === "string" && typeof data.appliedAt === "string" &&
    data.verification != null && typeof data.verification === "object" &&
    [data.warnings, data.pros, data.cons].every(items =>
      Array.isArray(items) && items.every(item => typeof item === "string")
    ) ? data : null;
}
